import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";

import {
    filterFns,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable
} from "@tanstack/react-table";
import classnames from "classnames";
import _ from "lodash";
import {Pagination} from "../../ReactTable/Pagination";
import {ReactTableHeader} from "../../ReactTable/ReactTableHeader";
import {ReactTableBody} from "../../ReactTable/ReactTableBody";
import {DEFAULT_ACTION_BUTTONS_PROPS, DefaultColumnFilter, getActionsColumn, toTitleCase} from "./helpers";
import PropTypes from "prop-types";
import {handleScrollChange} from "./scrollLogic";
import {wrapValueLabelInOptionStructure} from "../../../utils/maybeAddLabelToOptions";
import {useForm} from "react-hook-form";
import Modal from "react-modal";
import {EditRecordForm} from './EditRecordForm';
import {getJsonParamOrDefault, updateQueryStringForState} from "../../../utils/urlBuilder";


EditRecordForm.propTypes = {onSubmit: PropTypes.func};
const EditableReactTableV2 = (props) => {
    const {
        updatePageSize = () => undefined,
        updatePageIndex = () => undefined,
        initialState,
        children,
        className = "",
        showPagination = true,
        trClassName = "",
        getTrProps = () => ({}),
        filterable = false,
        hasStickyHeader = true,
        getTdProps = () => {
        },
        reactTableXCoordinateRef = undefined,
        editable = true,
        editModalProps,
        actionButtonsProps = DEFAULT_ACTION_BUTTONS_PROPS,
        validatePreSave = _.constant(true),
        editModalAdditionalClass = undefined,
        customActionComponent = undefined,
        saveSortStateToUrl = false,
        saveFilterStateToUrl = false,
        savePageIndexToUrl = false,
        prepareEditFormData = (data) => data,
        editTable = [],
        manualPagination = false,
        pageCount = undefined,
    } = props;

    const [mode, setMode] = React.useState(undefined);

    const stickyClassName = hasStickyHeader ? "sticky" : "";
    const stickyHeaderClassName = hasStickyHeader ? "headerForStickyHeader" : "";
    const stickyBodyClassName = hasStickyHeader ? "bodyForStickyHeader" : "";
    const initializeEditMode = (newMode, initialFormData) => {
        recordEditForm.reset(prepareEditFormData(initialFormData), {keepDefaultValues: true})
        setMode(newMode);
    }

    const defaultColumn = useMemo(() => ({filter: DefaultColumnFilter, minWidth: 30, maxWidth: 400}), []);

    const memoColumns = useMemo(() => {
        const mappedColumns = props.columns.map(x => ({...x, filterFn: x.filterFn || filterFns.includesString}));
        const containsColumnHeaders = props.columns.filter(x => x.columns);

        if (containsColumnHeaders.length > 0) {
            mappedColumns[0].columns = [
                ...getActionsColumn(editable, initializeEditMode, props.onRowDeleteConfirmed, actionButtonsProps, submitDeleteAllData, customActionComponent),
                ...mappedColumns[0].columns
            ]

            return mappedColumns
        } else {
            return [
                ...getActionsColumn(editable, initializeEditMode, props.onRowDeleteConfirmed, actionButtonsProps, submitDeleteAllData, customActionComponent),
                ...mappedColumns
            ];
        }

    }, [props.columns]);

    const stateProps = {}
    const onChangeProps = {}

    const [sorting, setSorting] = useState(saveSortStateToUrl ? getJsonParamOrDefault('sorting', initialState.sorting) : undefined)

    if (saveSortStateToUrl) {
        stateProps.sorting = sorting
        onChangeProps.onSortingChange = setSorting
    }

    useEffect(() => {
        if (!saveSortStateToUrl) return;
        updateQueryStringForState({sorting: JSON.stringify(sorting)})
    }, [saveSortStateToUrl, sorting])

    const [pagination, setPagination] = useState(savePageIndexToUrl ? {
        ...(initialState?.pagination || {}),
        pageIndex: getJsonParamOrDefault('pageIndex', initialState?.pagination?.pageIndex) || 0,
    } : undefined)

    if (savePageIndexToUrl) {
        stateProps.pagination = pagination
        onChangeProps.onPaginationChange = setPagination
    }

    useEffect(() => {
        if (!savePageIndexToUrl) return;
        updateQueryStringForState({pageIndex: pagination?.pageIndex})
    }, [savePageIndexToUrl, pagination])

    const [columnFilters, setColumnFilters] = useState(saveFilterStateToUrl ? getJsonParamOrDefault('filters', []) : (initialState?.columnFilters || []))
    if (saveFilterStateToUrl) {
        stateProps.columnFilters = columnFilters
        onChangeProps.onColumnFiltersChange = setColumnFilters
    }
    useEffect(() => {
        if (!saveFilterStateToUrl) return;
        updateQueryStringForState({filters: JSON.stringify(columnFilters)})
    }, [saveFilterStateToUrl, columnFilters])

    let table = useReactTable(
        {
            columns: memoColumns,
            data: props.data || [],
            defaultColumn,
            initialState: initialState,
            ...(stateProps ? {state: stateProps} : {}),
            ...onChangeProps,
            enableColumnResizing: true,
            columnResizeMode: "onChange",
            getFilteredRowModel: getFilteredRowModel(),
            getPaginationRowModel: getPaginationRowModel(),
            getCoreRowModel: getCoreRowModel(),
            getSortedRowModel: getSortedRowModel(),
            manualPagination: manualPagination,
            pageCount: pageCount,
            autoResetPageIndex: false
        }
    );

    const state = table.getState();
    const {pagination: {pageIndex, pageSize}} = state;
    const updateTableXCoordinate = event => {
        if (reactTableXCoordinateRef) {
            reactTableXCoordinateRef.current = event.currentTarget.scrollLeft;
        }
    };

    useLayoutEffect(() => {
        if (!_.isEmpty(reactTableXCoordinateRef)) {
            const rtTable = document.getElementsByClassName("rt-table")[0];
            rtTable.scrollLeft = reactTableXCoordinateRef.current;
        }
    }, []);

    const updatePageIndexRef = useRef(updatePageIndex);
    updatePageIndexRef.current = updatePageIndex;
    const updatePageSizeRef = useRef(updatePageSize);
    updatePageSizeRef.current = updatePageSize;

    useEffect(() => {
        updatePageIndexRef.current(pageIndex);
        updatePageSizeRef.current(pageSize);
    }, [pageIndex, pageSize]);

    useEffect(() => {
        if (!hasStickyHeader) return;
        return handleScrollChange();
    }, [hasStickyHeader, pageIndex, pageSize]);


    async function submitUpdatedData(newData) {
        try {
            if (validatePreSave({
                newData,
                rows: table.getCoreRowModel().rows,
                previousData: props.data,
                form: recordEditForm
            })) {
                await props.onRowEditConfirmed(newData, table.getCoreRowModel().rows);
                cancellationCallback();
            }
        } catch (e) {
            recordEditForm.setError('apiError', {type: 'server', message: e.message});
            console.error(e);
        }
    }

    function cancellationCallback() {
        setMode(undefined)
    }

    function submitDeleteAllData(dataToDelete) {
        props.onDeleteAllConfirmed(dataToDelete);
    }

    const derivedDefaultValues = editTable
        .filter(({defaultValue}) => defaultValue !== undefined)
        .map(({id, defaultValue}) => ({[id]: defaultValue}))
        .reduce((acc, curr) => Object.assign(acc, curr), {})

    const recordEditForm = useForm({
        defaultValues: {...derivedDefaultValues},
    });


    const isFirstColumnFiltersRender = React.useRef(true);
    useEffect(() => {
        if (isFirstColumnFiltersRender.current) {
            isFirstColumnFiltersRender.current = false;
            return;
        }
        if (!_.isEmpty(table.getState().rowSelection)) {
            table.resetRowSelection();
        }
    }, [state.columnFilters])

    return <>
        <div className={"react-table-wrapper"} data-testid={"react-table-wrapper"}>
            {children && children({rows: table.getFilteredRowModel().rows, table})}
            <div className={classnames(className, stickyClassName, "ReactTable7")}>
                <div className={"rt-table"}
                     data-testref={reactTableXCoordinateRef}
                     onScroll={updateTableXCoordinate}>
                    <div className={classnames(stickyHeaderClassName, "rt-thead -header")}>
                        {
                            table.getHeaderGroups().map((headerGroup, i) => <ReactTableHeader key={`headerGroup_${i}`}
                                                                                              headerGroup={headerGroup}
                                                                                              filterable={filterable}
                                                                                              table={table}/>)
                        }
                    </div>
                    <div className={classnames(stickyBodyClassName, "rt-tbody")}>
                        <div className="rt-tr-group" data-testid={"react-table-body"}>
                            {
                                table.getRowModel().rows.map((row, ind) => <ReactTableBody key={row.id}
                                                                                           row={row}
                                                                                           index={ind}
                                                                                           trClassName={trClassName}
                                                                                           getTrProps={getTrProps}
                                                                                           getTdProps={getTdProps}
                                                                                           table={table}/>)
                            }
                        </div>
                    </div>
                </div>
                {showPagination && (
                    <Pagination
                        firstPageOnClick={() => table.setPageIndex(0)}
                        canPreviousPage={table.getCanPreviousPage()}
                        previousPageOnClick={() => table.previousPage()}
                        pageIndex={pageIndex}
                        pageOptions={table.getPageOptions()}
                        value={pageSize}
                        onChange={(option) => table.setPageSize(Number(option.value))}
                        dropdownCallBack={(pageSize) => wrapValueLabelInOptionStructure(pageSize, `Show ${pageSize}`)}
                        nextPageOnClick={() => table.nextPage()}
                        canNextPage={table.getCanNextPage()}
                        lastPageOnClick={() => table.setPageIndex(table.getPageCount() - 1)
                        }/>)}
            </div>
        </div>
        <Modal
            clickable
            isOpen={!!mode}
            className={`react-table-edit-modal ${editModalAdditionalClass}`}
            overlayClassName="Overlay"
            ariaHideApp={false}
            onRequestClose={() => cancellationCallback()}
        >
            <div><h5 className={"modal-header-text"}>{`${toTitleCase(mode)} Record`}</h5></div>
            <EditRecordForm
                form={recordEditForm}
                submitUpdatedData={submitUpdatedData}
                cancellationCallback={cancellationCallback}
                dropdownConfig={editModalProps}
                columns={props.columns}
                editTable={editTable}
            />
        </Modal>
    </>
}

const actionButtonsPropsShape = {
    showDeleteAllButton: PropTypes.bool,
    showDeleteButton: PropTypes.bool,
    showAddButton: PropTypes.bool,
    showEditButton: PropTypes.bool,
    sticky: PropTypes.bool
};

const paginationShape = {
    pageSize: PropTypes.number,
    pageIndex: PropTypes.number
};

const initialState = {
    columnFilters: PropTypes.arrayOf(PropTypes.object),
    sorting: PropTypes.arrayOf(PropTypes.object),
    pagination: PropTypes.shape(paginationShape)
};

EditableReactTableV2.propTypes = {
    editable: PropTypes.bool,
    customColumns: PropTypes.array,
    editModalAdditionalClass: PropTypes.string,
    updatePageSize: PropTypes.func,
    updatePageIndex: PropTypes.func,
    initialState: PropTypes.shape(initialState),
    children: PropTypes.any,
    className: PropTypes.string,
    showPagination: PropTypes.bool,
    trClassName: PropTypes.string,
    getTrProps: PropTypes.func,
    filterable: PropTypes.bool,
    hasStickyHeader: PropTypes.bool,
    getTdProps: PropTypes.func,
    reactTableXCoordinateRef: PropTypes.object,
    actionButtonsProps: PropTypes.shape(actionButtonsPropsShape),
    customActionComponent: PropTypes.func,
    onRowEditConfirmed: PropTypes.func,
    onRowDeleteConfirmed: PropTypes.func,
    onDeleteAllConfirmed: PropTypes.func,
    saveSortStateToUrl: PropTypes.bool,
    saveFilterStateToUrl: PropTypes.bool,
    savePageIndexToUrl: PropTypes.bool,
    manualPagination: PropTypes.bool,
    pageCount: PropTypes.number,
    prepareEditFormData: PropTypes.func,
};
export default EditableReactTableV2;